import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Target, 
  Shield, 
  Headphones, 
  Globe, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const About = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const content = {
    pt: {
      badge: 'QUEM SOMOS',
      title: 'Seu parceiro em',
      titleHighlight: 'Comércio Exterior',
      subtitle: 'Simplificamos a logística internacional para que você foque no que importa: fazer seu negócio crescer.',
      description: 'A Well Cargo atua como extensão da sua equipe, oferecendo soluções personalizadas do planejamento à entrega.',
      cta: 'Conheça nossa história',
      features: [
        {
          icon: Users,
          title: 'Atendimento Consultivo',
          desc: 'Equipe dedicada que entende seu negócio e oferece soluções sob medida',
        },
        {
          icon: Target,
          title: 'Foco em Resultados',
          desc: 'Processos otimizados para garantir eficiência e redução de custos',
        },
        {
          icon: Shield,
          title: 'Segurança Total',
          desc: 'Sua carga protegida em todas as etapas do transporte internacional',
        },
        {
          icon: Globe,
          title: 'Presença Global',
          desc: 'Rede de parceiros em 196 países através da WCA World',
        },
      ],
      benefits: [
        '15+ anos de experiência no mercado',
        'Equipe técnica especializada',
        'Suporte 24/7 em PT, EN e ES',
      ],
      card: {
        title: 'Por que a Well Cargo?',
        items: [
          'Soluções personalizadas',
          'Preços competitivos',
          'Agilidade nos processos',
          'Transparência total',
        ]
      }
    },
    en: {
      badge: 'WHO WE ARE',
      title: 'Your partner in',
      titleHighlight: 'Foreign Trade',
      subtitle: 'We simplify international logistics so you can focus on what matters: growing your business.',
      description: 'Well Cargo acts as an extension of your team, offering personalized solutions from planning to delivery.',
      cta: 'Learn our story',
      features: [
        {
          icon: Users,
          title: 'Consultative Service',
          desc: 'Dedicated team that understands your business and offers tailored solutions',
        },
        {
          icon: Target,
          title: 'Results Focused',
          desc: 'Optimized processes to ensure efficiency and cost reduction',
        },
        {
          icon: Shield,
          title: 'Total Security',
          desc: 'Your cargo protected at all stages of international transport',
        },
        {
          icon: Globe,
          title: 'Global Presence',
          desc: 'Partner network in 196 countries through WCA World',
        },
      ],
      benefits: [
        '15+ years of market experience',
        'Specialized technical team',
        '24/7 support in PT, EN & ES',
      ],
      card: {
        title: 'Why Well Cargo?',
        items: [
          'Personalized solutions',
          'Competitive prices',
          'Process agility',
          'Total transparency',
        ]
      }
    },
    es: {
      badge: 'QUIÉNES SOMOS',
      title: 'Tu socio en',
      titleHighlight: 'Comercio Exterior',
      subtitle: 'Simplificamos la logística internacional para que te enfoques en lo que importa: hacer crecer tu negocio.',
      description: 'Well Cargo actúa como extensión de tu equipo, ofreciendo soluciones personalizadas desde la planificación hasta la entrega.',
      cta: 'Conoce nuestra historia',
      features: [
        {
          icon: Users,
          title: 'Atención Consultiva',
          desc: 'Equipo dedicado que entiende tu negocio y ofrece soluciones a medida',
        },
        {
          icon: Target,
          title: 'Enfoque en Resultados',
          desc: 'Procesos optimizados para garantizar eficiencia y reducción de costos',
        },
        {
          icon: Shield,
          title: 'Seguridad Total',
          desc: 'Tu carga protegida en todas las etapas del transporte internacional',
        },
        {
          icon: Globe,
          title: 'Presencia Global',
          desc: 'Red de socios en 196 países a través de WCA World',
        },
      ],
      benefits: [
        '15+ años de experiencia',
        'Equipo técnico especializado',
        'Soporte 24/7 en PT, EN y ES',
      ],
      card: {
        title: '¿Por qué Well Cargo?',
        items: [
          'Soluciones personalizadas',
          'Precios competitivos',
          'Agilidad en procesos',
          'Transparencia total',
        ]
      }
    },
  };

  const c = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-white dark:bg-gray-900 relative overflow-hidden transition-colors duration-300">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#3ECDA3]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block bg-[#3ECDA3]/10 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6">
            {c.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-4">
            {c.title}{' '}
            <span className="text-[#3ECDA3] italic">{c.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            {c.subtitle}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Features Grid */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {c.features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className="group p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl hover:bg-[#3ECDA3]/5 dark:hover:bg-[#3ECDA3]/10 border border-transparent hover:border-[#3ECDA3]/20 transition-all duration-300"
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="w-12 h-12 bg-[#3ECDA3]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#3ECDA3] transition-colors duration-300">
                      <IconComponent className="w-6 h-6 text-[#3ECDA3] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Benefits List */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[#3ECDA3]/5 to-transparent rounded-2xl">
              <div className="grid grid-cols-2 gap-3">
                {c.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#3ECDA3] flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Visual Card */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              {/* Main Image Card */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80"
                  alt="Logistics"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a6b5c] via-[#1a6b5c]/60 to-transparent" />
                
                {/* Content Overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  {/* Logo */}
                  <div className="flex items-center gap-2 mb-6">
                    <svg className="w-8 h-8" viewBox="0 0 40 40" fill="none">
                      <path d="M5 10L20 25L25 20" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 10L30 25L35 20" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-white text-xl font-light">well cargo</span>
                  </div>

                  {/* Card Content */}
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#3ECDA3]" />
                      {c.card.title}
                    </h3>
                    <div className="space-y-3">
                      {c.card.items.map((item, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#3ECDA3] rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-white/90 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-14 left-4 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-5 border border-gray-100 dark:border-gray-700 hidden lg:block z-10">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#3ECDA3] rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">196</div>
                    <div className="text-gray-500 dark:text-gray-400 text-sm">Países conectados</div>
                  </div>
                </div>
              </div>

              {/* Floating Support Card */}
              <div className="absolute -top-4 right-4 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 border border-gray-100 dark:border-gray-700 hidden lg:block z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white">Suporte 24/7</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Português • English • Español</div>
                    <div className="text-xs text-green-500">Online agora</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <Link
                to="/sobre"
                className="group inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-full font-medium hover:gap-4 transition-all duration-300 hover:shadow-lg"
              >
                {c.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
